// WP13 Market landing — authoritative Commit-6 structural product.
// Replaces the legacy POC market screen (editorial pattern subsets, raw
// weekly activity and evidence totals are no longer the primary product).
import React from "react";
import { Link } from "react-router-dom";
import { useWp12 } from "./useWp12";
import { MarketLayout } from "./MarketTree";
import { StateGrid } from "./StateGrid";
import { LeadersChart } from "./LeadersChart";
import { zoneColor } from "./LeadersChart";
import { patternDisplayTitle } from "./TopPatternsTable";

const firstSentence = (t) => { const m = String(t || "").match(/^.*?[.!?](?=\s|$)/s); return m ? m[0] : t; };
const readable = (g) => g.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");

export default function MarketLanding() {
    const { status, data: market } = useWp12("market");
    const homeRes = useWp12("home");

    if (status === "loading") return <div className="p-8 text-sm text-[hsl(var(--mute))]">Loading…</div>;
    if (!market) return <div className="p-8 text-sm text-[hsl(var(--warning))]">The active publication package is unavailable.</div>;

    const zones = market.marketZones || [];
    const total = zones.reduce((s, z) => s + (z.counts?.patternCount || 0), 0);
    const topTwo = (zk) => (market.patternCards || [])
        .filter((c) => c.marketZoneKey === zk)
        .sort((a, b) => (b.strength?.displayScoreExact ?? -1) - (a.strength?.displayScoreExact ?? -1))
        .slice(0, 2);

    return (
        <MarketLayout market={market}>
            <div className="space-y-8" data-testid="wp13-market-landing">
                <div>
                    <div className="eyebrow">Market structure</div>
                    <h1 className="text-xl font-semibold mt-0.5">Digital-asset market</h1>
                    <div className="num text-[10px] text-[hsl(var(--mute))] mt-1" data-testid="wp13-market-total">
                        {total} published patterns across {zones.length} market zones · {(market.useCases || []).length} use cases
                    </div>
                </div>

                {/* Cross-zone comparison — same authoritative leaders as Overview */}
                {homeRes.data?.crossZoneLeaders && (
                    <section className="space-y-2" data-testid="wp13-market-leaders">
                        <h2 className="text-sm font-semibold">Leading pattern per market zone</h2>
                        <p className="text-[10px] text-[hsl(var(--mute))]">Strongest published pattern in each Market Zone · 12-week score history.</p>
                        <LeadersChart leaders={homeRes.data.crossZoneLeaders} horizonWeeks={12} />
                    </section>
                )}

                {/* Six authoritative zone cards */}
                <section className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="wp13-market-zones">
                    {zones.map((z) => {
                        const color = zoneColor(z.presentationColorToken);
                        const h4 = (z.horizons || []).find((h) => h.horizonWeeks === 4);
                        const h12 = (z.horizons || []).find((h) => h.horizonWeeks === 12);
                        const brief = z.structuralBrief?.structuralStateBrief;
                        return (
                            <div key={z.scopeKey} className="card-flat p-4 border-t-[3px]" style={{ borderTopColor: color }} data-testid={`wp13-market-zone-${z.scopeKey}`}>
                                <div className="flex items-baseline justify-between gap-3">
                                    <Link to={`/market-v2/market?zone=${encodeURIComponent(z.scopeKey)}`}
                                          className="text-[13px] font-semibold hover:text-[hsl(var(--accent-ink))] hover:underline">{z.label}</Link>
                                    <span className="flex-shrink-0">
                                        <span className="num text-lg font-semibold">{z.strength?.displayScoreRounded ?? "—"}</span>
                                        <span className="text-[9px] text-[hsl(var(--mute))] ml-1">{String(z.strength?.scoreBand || "").replace(/_/g, " ").toLowerCase()}</span>
                                    </span>
                                </div>
                                <div className="num text-[9px] text-[hsl(var(--mute))] mt-0.5">
                                    {z.counts?.patternCount ?? "—"} patterns · {z.counts?.useCaseCount ?? "—"} use cases
                                </div>
                                <div className="mt-2.5">
                                    <StateGrid items={[
                                        { label: "4w trend", value: h4?.trend?.primaryTrendClass },
                                        { label: "12w trend", value: h12?.trend?.primaryTrendClass },
                                        { label: "Velocity", value: h12?.trend?.velocityState },
                                        { label: "Breadth", value: h12?.breadth?.actorTrendClass },
                                        { label: "Persistence", value: h12?.trend?.persistenceState },
                                    ]} />
                                </div>
                                {brief && <p className="text-[10.5px] text-[hsl(var(--ink-soft))] leading-snug mt-2.5" title={brief}>{firstSentence(brief)}</p>}
                                <div className="mt-2.5 divide-y divide-[hsl(var(--line))] border-t border-[hsl(var(--line))]">
                                    {topTwo(z.scopeKey).map((c) => (
                                        <Link key={c.canonicalPatternId} to={`/market-v2/patterns/${encodeURIComponent(c.canonicalPatternId)}`}
                                              className="flex items-baseline justify-between gap-3 py-1.5 hover:text-[hsl(var(--accent-ink))] group">
                                            <span className="text-[10.5px] truncate group-hover:underline">{patternDisplayTitle(c)}</span>
                                            <span className="num text-[10px] font-medium flex-shrink-0">{c.strength?.displayScoreExact?.toFixed(1)}</span>
                                        </Link>
                                    ))}
                                </div>
                                {(z.geographyRefs || []).length > 0 && (
                                    <div className="text-[8.5px] text-[hsl(var(--mute))] mt-2 truncate">
                                        {z.geographyRefs.map(readable).join(" · ")}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </section>
            </div>
        </MarketLayout>
    );
}
